import React from "react";
import { useLang } from "@/context/LanguageContext";
import { PawIcon, PawScatter, HeartIcon } from "@/components/Icons";
import Reveal from "@/components/Reveal";

const Hero = () => {
  const { t } = useLang();

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="hero-bg relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28"
    >
      <PawScatter />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <div className="inline-flex items-center gap-2 bg-white/85 backdrop-blur-sm px-4 py-2 rounded-full text-[#6b3f2a] font-bold text-sm shadow-sm">
              <HeartIcon size={16} className="text-[#f4956a]" />
              {t.hero.badge}
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-display leading-[1.05]">
              {t.hero.title}
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 text-base sm:text-lg text-[#6b3f2a]/85 max-w-2xl leading-relaxed">
              {t.hero.subtitle}
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#services" className="btn-primary" data-testid="hero-cta-primary">
                <PawIcon size={16} /> {t.hero.ctaPrimary}
              </a>
              <a href="#contact" className="btn-outline" data-testid="hero-cta-secondary">
                {t.hero.ctaSecondary}
              </a>
            </div>
          </Reveal>
        </div>

        {/* Illustration column */}
        <Reveal delay={400} className="lg:col-span-5 relative flex items-center justify-center">
          <div className="relative w-full max-w-md aspect-square">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#fff1c2] via-[#f7c5c0] to-[#f4956a] blur-2xl opacity-60" />
            <div className="absolute inset-4 rounded-full bg-white/85 backdrop-blur-sm shadow-xl overflow-hidden flex items-end justify-center">
              <svg
                viewBox="0 0 400 320"
                preserveAspectRatio="xMidYMax meet"
                className="w-[92%] h-[88%]"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                {/* ground shadow */}
                <ellipse cx="200" cy="300" rx="160" ry="10" fill="#6b3f2a" opacity="0.08" />

                {/* DOG (left) */}
                <g transform="translate(40,90)">
                <g className="animate-float">
                  <ellipse cx="60" cy="140" rx="58" ry="40" fill="#e8b48c" />
                  <circle cx="60" cy="80" r="50" fill="#f0c39a" />
                  <ellipse cx="20" cy="62" rx="14" ry="24" fill="#c98b5d" transform="rotate(-20 20 62)" />
                  <ellipse cx="100" cy="62" rx="14" ry="24" fill="#c98b5d" transform="rotate(20 100 62)" />
                  <circle cx="42" cy="82" r="4.5" fill="#3a2418" />
                  <circle cx="78" cy="82" r="4.5" fill="#3a2418" />
                  <circle cx="44" cy="80" r="1.4" fill="#fff" />
                  <circle cx="80" cy="80" r="1.4" fill="#fff" />
                  <ellipse cx="60" cy="98" rx="6" ry="4" fill="#3a2418" />
                  <path d="M54 108 Q60 116 66 108" stroke="#3a2418" strokeWidth="2.2" fill="none" strokeLinecap="round" />
                  <path d="M57 112 Q60 120 63 112 Z" fill="#f4956a" />
                  <ellipse cx="30" cy="178" rx="12" ry="9" fill="#c98b5d" />
                  <ellipse cx="90" cy="178" rx="12" ry="9" fill="#c98b5d" />
                  <rect x="30" y="126" width="60" height="9" rx="4" fill="#f4956a" />
                  <circle cx="60" cy="135" r="5" fill="#fff1c2" />
                </g>
                </g>

                {/* CAT (right) */}
                <g transform="translate(230,110)">
                <g className="animate-float-slow">
                  <ellipse cx="60" cy="120" rx="58" ry="40" fill="#fbe1de" />
                  <circle cx="60" cy="60" r="46" fill="#f7c5c0" />
                  <path d="M22 36 L30 6 L48 32 Z" fill="#f7c5c0" />
                  <path d="M98 36 L90 6 L72 32 Z" fill="#f7c5c0" />
                  <path d="M28 30 L32 16 L40 30 Z" fill="#f4956a" />
                  <path d="M92 30 L88 16 L80 30 Z" fill="#f4956a" />
                  <ellipse cx="44" cy="62" rx="4.5" ry="7" fill="#3a2418" />
                  <ellipse cx="76" cy="62" rx="4.5" ry="7" fill="#3a2418" />
                  <circle cx="45.5" cy="60" r="1.6" fill="#fff" />
                  <circle cx="77.5" cy="60" r="1.6" fill="#fff" />
                  <path d="M54 74 L66 74 L60 80 Z" fill="#6b3f2a" />
                  <path d="M60 80 Q55 86 50 84" stroke="#6b3f2a" strokeWidth="1.8" fill="none" strokeLinecap="round" />
                  <path d="M60 80 Q65 86 70 84" stroke="#6b3f2a" strokeWidth="1.8" fill="none" strokeLinecap="round" />
                  <line x1="14" y1="70" x2="36" y2="72" stroke="#6b3f2a" strokeWidth="1" />
                  <line x1="14" y1="76" x2="36" y2="76" stroke="#6b3f2a" strokeWidth="1" />
                  <line x1="106" y1="70" x2="84" y2="72" stroke="#6b3f2a" strokeWidth="1" />
                  <line x1="106" y1="76" x2="84" y2="76" stroke="#6b3f2a" strokeWidth="1" />
                  <path d="M118 132 Q150 130 144 90" stroke="#f7c5c0" strokeWidth="18" fill="none" strokeLinecap="round" />
                </g>
                </g>

                {/* hearts */}
                <g>
                  <path d="M188 50 a9 9 0 1 1 18 0 c0 9 -9 16 -9 16 s -9 -7 -9 -16 z" fill="#f4956a" opacity="0.85" />
                  <path d="M340 100 a6 6 0 1 1 12 0 c0 6 -6 11 -6 11 s -6 -5 -6 -11 z" fill="#a8d5ba" opacity="0.85" />
                  <path d="M60 60 a5 5 0 1 1 10 0 c0 5 -5 9 -5 9 s -5 -4 -5 -9 z" fill="#a8d5ba" opacity="0.8" />
                </g>
              </svg>
            </div>

            {/* Babypause sticker */}
            <div
              className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 z-10 rotate-[12deg] hover:rotate-[8deg] transition-transform"
              data-testid="babypause-sticker"
            >
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-[#f4956a] blur-md opacity-50" />
                <div className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-gradient-to-br from-[#fff1c2] to-[#f7d97a] border-4 border-white shadow-2xl flex flex-col items-center justify-center text-center px-2">
                  <span className="text-2xl sm:text-3xl">🍼</span>
                  <span className="font-display text-[#6b3f2a] text-sm sm:text-base leading-tight mt-1">
                    Babypause
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Hero;
